import { configureAbly, useChannel } from '@ably-labs/react-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from 'common/utils/utils';
import { QUERIES_KEYS } from 'constant';
import { APP_CONSTANTS } from 'constants/app';
import { CHANNEL_MESSAGE_TYPES } from 'constants/common';
import {
  FIRST_LEVEL_CATEGORY_FIELD_ID,
  SECOND_LEVEL_CATEGORY_FIELD_ID,
  THIRD_LEVEL_CATEGORY_FIELD_ID,
} from 'features/header/categories/constants';
import useTexts from 'features/loggedInRoutesPanel/useTexts';
import {
  ASSET_FIELD_ID,
  ASSIGNED_GROUP_FIELD_ID,
  ASSIGNEE_FIELD_ID,
  CI_FIELD_ID,
  PROJECT_FIELD_ID,
  REQUEST_USER_FIELD_ID,
  TASK_FIELD_ID,
} from 'features/queue/grid/constants';
import { LIMITED_DATA_BY_COMPANY_ALL_FIELDS } from 'features/settings/constants';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isLocalHost } from 'services/localhost';
import { showPrompt } from 'store/messagePromptSlice';
import { details as userDetailsSelector } from 'store/userSlice';

const accountId = localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY);
const QUEUE_CHANNEL_NAME = accountId;
const targetChannel = localStorage.getItem(APP_CONSTANTS.ACCOUNT_TARGET);

export const useGeneralWebSocketDataSync = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isReloadPromptOpen, setIsReloadPromptOpen] = useState(false);
  const userDetails = useSelector(userDetailsSelector);
  const userDetailsRef = useRef(userDetails);
  const {
    updatedPermissions: { title, description, refreshPageButton },
  } = useTexts();

  useEffect(() => {
    userDetailsRef.current = userDetails;
  }, [userDetails]);

  const isLocalEnvironment = isLocalHost();

  configureAbly({
    autoConnect: !isLocalEnvironment,
    authCallback: (_, callback) => {
      (async () => {
        let tokenRequest;
        try {
          const response = await fetch('/spaces/api/authentication/websocket/token');
          tokenRequest = await response.json();
          tokenRequest['clientId'] = getCookie(APP_CONSTANTS.SESSION_ID);
          callback(null, tokenRequest);
        } catch (e) {
          console.error('Error occurred during fetching token for ably', e);
          callback(e, null);
        }
      })();
    },
    recover: (_, cb) => {
      cb(true);
    },
  });

  useChannel(targetChannel, ({ name, data }) => {
    setIsReloadPromptOpen(true);
    console.log('Websocket Target Channel: useChannel event commited', { target: targetChannel, name, data });
  });

  useChannel(QUEUE_CHANNEL_NAME, ({ name, data }) => {
    if (name === CHANNEL_MESSAGE_TYPES.UPDATE_SETTINGS) {
      //We clean attributes with timeout because Wescoket works much faster than settings changes getting applied on backend.
      //In that case we are not getting updated values on attributes but old one.
      setTimeout(() => {
        switch (data?.type) {
          case 'defaultTemplate':
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.TEMPLATES_LIST] });
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.DEFAULT_TEMPLATE] });
            break;
          case 'applicationData':
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.APPLICATION_DATA] });
            break;
          case 'srSubTab':
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.ATTRIBUTES, data?.listName] });
            localStorage.setItem('cacheKey', String(Math.random()));
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.FIELD_ATTRIBUTE] });
            break;
          case 'uploadTranslationFile':
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.RESOURCE_BUNDLE] });
            break;
          case 'uploadStatusSettings':
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.STATUS_SETTINGS] });
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.FIELD_ATTRIBUTE, 16] });
            break;
          case 'categoriesUpdate':
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.CATEGORIES] });
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.FIELD_ATTRIBUTE] });
            break;
          case LIMITED_DATA_BY_COMPANY_ALL_FIELDS: {
            const fieldIdsToClearCache = [
              REQUEST_USER_FIELD_ID,
              ASSIGNEE_FIELD_ID,
              ASSIGNED_GROUP_FIELD_ID,
              PROJECT_FIELD_ID,
              TASK_FIELD_ID,
              ASSET_FIELD_ID,
              CI_FIELD_ID,
              FIRST_LEVEL_CATEGORY_FIELD_ID,
              SECOND_LEVEL_CATEGORY_FIELD_ID,
              THIRD_LEVEL_CATEGORY_FIELD_ID,
            ];

            if (data.username === userDetailsRef?.current?.userName) {
              fieldIdsToClearCache.forEach((fieldId) => {
                queryClient.removeQueries({ queryKey: [QUERIES_KEYS.FIELD_ATTRIBUTE, fieldId] });
              });

              queryClient.removeQueries({ queryKey: [QUERIES_KEYS.ATTRIBUTES] });

              dispatch(
                showPrompt({
                  title,
                  descriptionText: description,
                  btnOkText: refreshPageButton,
                  showCancelBtn: false,
                  showIcon: true,
                  onClickFunctionName: LIMITED_DATA_BY_COMPANY_ALL_FIELDS,
                  okCb: () => {
                    window.location.reload();
                  },
                }),
              );
            }
            break;
          }
          default:
            localStorage.setItem('cacheKey', String(Math.random()));
            queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.FIELD_ATTRIBUTE] });
            break;
        }
      }, 2000);
    }
  });

  return { isReloadPromptOpen, setReloadPromptOpen: setIsReloadPromptOpen };
};

export default useGeneralWebSocketDataSync;
